import { useState, useEffect } from "react";
import { Network, Award, Briefcase, CheckCircle } from "lucide-react";
import AlumniProfile from "../components/AlumniProfile";
import AlumniJoinForm from "../components/AlumniJoinForm";

const Alumni = () => {
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const objectives = [
    "Foster lifelong relationships between the department, its alumni, and current students.",
    "Facilitate knowledge sharing, mentorship, and professional development.",
    "Support the department in academic, research, and industrial collaborations.",
    "Create opportunities for alumni to contribute towards career guidance of undergraduates.",
    "Strengthen the department’s visibility and reputation locally and internationally.",
  ];

  useEffect(() => {
    // Trigger animations after component mounts
    setIsLoaded(true);

    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 z-0"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')] opacity-10 bg-cover bg-center mix-blend-overlay"></div>
        <div className="absolute top-40 right-20 w-64 h-64 rounded-full bg-blue-600/10 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-amber-600/10 blur-3xl"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div
              className={`space-y-10 ${
                isLoaded ? "animate-[slideUpFade_0.8s_ease-out]" : "opacity-0"
              }`}
            >
              {/* Logo and Title */}
              <div className="space-y-6">
                <div className="inline-block">
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="text-white bg-clip-text bg-gradient-to-r from-white to-gray-300">
                      Alumini Association
                    </span>
                  </h1>
                  <div className="flex items-center space-x-2 mt-3">
                    <div className="w-8 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"></div>
                    <div className="w-4 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full opacity-70"></div>
                    <div className="w-2 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full opacity-40"></div>
                  </div>
                </div>

                <div>
                  <h2 className="text-3xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400 leading-tight">
                    Department Of Mechanical Engineering
                  </h2>
                  <p className="text-amber-400 font-medium mt-2 text-lg">
                    Connecting Past, Present & Future
                  </p>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4 backdrop-blur-sm bg-white/5 p-6 rounded-xl border border-white/10">
                <p className="text-gray-300 text-lg leading-relaxed">
                  The Alumni Association of the Department of Mechanical
                  Engineering, University of Moratuwa is a dedicated body that
                  connects past graduates with the department and its ongoing
                  initiatives. It serves as a bridge between generations of
                  engineers, creating opportunities for knowledge sharing,
                  collaboration, and mutual growth.
                </p>
              </div>

              {/* Call to Action */}
              <div className="pt-4 flex items-center space-x-4">
                <button
                  className="group relative overflow-hidden px-8 py-4 rounded-4xl bg-gradient-to-br from-amber-500 to-orange-500 text-black font-semibold text-lg shadow-lg shadow-amber-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/40 hover:scale-105 transform active:scale-95 before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-gradient-to-r before:from-white/20 before:via-white/50 before:to-white/20 before:transition-all before:duration-700 before:skew-x-[-25deg] hover:before:left-full"
                  onClick={() => setShowJoinForm(true)}
                >
                  {/* Button shine effect */}
                  <span className="absolute inset-0 w-full h-full bg-white/30 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>

                  <span className="relative flex items-center justify-center space-x-3">
                    <span>Join Our Network</span>
                    <svg
                      className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div
              className={`relative rounded-2xl overflow-hidden shadow-2xl transform lg:translate-y-0 translate-y-4 ${
                isLoaded
                  ? "animate-[slideUpFade_1s_ease-out_0.2s_backwards]"
                  : "opacity-0"
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-blue-600/10 mix-blend-overlay z-10"></div>
              <img
                src="/alumni_1.jpg"
                alt="Alumni Networking Event"
                className="w-full h-[450px] object-cover transform scale-105 hover:scale-110 transition-transform duration-700 ease-in-out"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Image */}
            <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/alumni_2.jpg"
                alt="Professional Development"
                className="w-full h-[500px] object-cover transform"
              />
            </div>

            {/* Right Content */}
            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="text-4xl lg:text-5xl font-bold text-white">
                Our Objectives
              </h2>

              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                {objectives.map((obj, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle className="text-blue-500 mt-1" size={20} />
                    <span>{obj}</span>
                  </div>
                ))}
              </div>

              {/* Objective highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6">
                <div className="bg-slate-700 rounded-lg p-4 text-center shadow-md">
                  <Network className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                  <h4 className="font-semibold text-white">Networking</h4>
                  <p className="text-sm text-gray-400">
                    Build professional connections
                  </p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4 text-center shadow-md">
                  <Award className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <h4 className="font-semibold text-white">Excellence</h4>
                  <p className="text-sm text-gray-400">
                    Promote academic achievements
                  </p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4 text-center shadow-md">
                  <Briefcase className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <h4 className="font-semibold text-white">Collaboration</h4>
                  <p className="text-sm text-gray-400">
                    Industry-academia partnership
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alumni Profiles Section */}
      <div className="py-2 px-4 sm:px-6 lg:px-8 bg-slate-800 text-center">
        <h2 className="text-4xl font-bold text-white">Alumni Profiles</h2>
      </div>

      {/* Alumni Profiles */}
      <AlumniProfile />

      {/* Join Form Modal */}
      {showJoinForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50">
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-6xl relative p-6 flex flex-col overflow-auto max-h-[90vh] hide-scrollbar
      transform transition-all duration-500 ease-out
      translate-y-10 opacity-0
      animate-slideup"
          >
            {/* Close button */}

            <button
              className="absolute top-2.5 right-2.5 sm:top-4 sm:right-4 text-gray-500 hover:text-gray-700 font-bold text-2xl sm:text-4xl w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/80 hover:bg-gray-100 shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 z-50"
              style={{ lineHeight: 1 }}
              aria-label="Close alumni join form"
              onClick={() => setShowJoinForm(false)}
            >
              &times;
            </button>
            {/* Form Component */}
            <AlumniJoinForm setShowJoinForm={setShowJoinForm} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Alumni;
